import { StatusMessage } from "@whitehaven/ui";
import { useSyncExternalStore } from "react";
import { focusTarget } from "./focus-management";
import {
  shouldShowServiceWorkerUpdate,
  type ServiceWorkerUpdateSnapshot,
} from "./service-worker-update-model.ts";
import {
  portalServiceWorkerUpdates,
  type ServiceWorkerUpdateManager,
} from "./service-worker-updates.ts";

interface ServiceWorkerUpdateNoticeProps {
  readonly manager?: ServiceWorkerUpdateManager;
}

interface UpdateNoticeCopy {
  readonly title: string;
  readonly body: string;
  readonly tone: "neutral" | "positive" | "warning" | "critical";
}

export function getServiceWorkerUpdateNoticeCopy(
  snapshot: ServiceWorkerUpdateSnapshot,
): UpdateNoticeCopy {
  if (snapshot.status === "ready") {
    return {
      title: "Portal update ready",
      body: "Reload when you can safely leave this page.",
      tone: "positive",
    };
  }

  if (snapshot.status === "activating") {
    return {
      title: "Updating the portal",
      body: "Keep this tab open. It will reload when the update is ready.",
      tone: "neutral",
    };
  }

  if (snapshot.status === "blocked") {
    return snapshot.code === "OFFLINE"
      ? {
          title: "Reconnect before updating",
          body: "Connect to the internet, then try the update again.",
          tone: "warning",
        }
      : {
          title: "Save your changes before updating",
          body: "Finish or save your work, then try the update again.",
          tone: "warning",
        };
  }

  if (snapshot.status === "refresh-required") {
    if (snapshot.code === "UNSAVED_WORK") {
      return {
        title: "Update installed",
        body: "Save your changes before reloading this tab.",
        tone: "warning",
      };
    }
    if (snapshot.code === "OFFLINE") {
      return {
        title: "Update installed",
        body: "Reconnect to the internet before reloading this tab.",
        tone: "warning",
      };
    }
    if (snapshot.code === "RELOAD_FAILED") {
      return {
        title: "Update installed",
        body: "This tab could not reload automatically. Reload it when you are ready.",
        tone: "warning",
      };
    }
    return {
      title: "Update installed",
      body: "Another tab may already be using it. Reload this tab when you are ready.",
      tone: "positive",
    };
  }

  const registrationError = [
    "REGISTRATION_FAILED",
    "UPDATE_CHECK_FAILED",
    "UPDATE_INSTALL_FAILED",
  ].includes(snapshot.code ?? "");
  return registrationError
    ? {
        title: "Update check unavailable",
        body: "You can keep working and check for an update again later.",
        tone: "warning",
      }
    : {
        title: "Update was not completed",
        body: "You can keep working and try the update again when you’re ready.",
        tone: "warning",
      };
}

export function ServiceWorkerUpdateNotice({
  manager = portalServiceWorkerUpdates,
}: ServiceWorkerUpdateNoticeProps) {
  const snapshot = useSyncExternalStore(
    manager.subscribe,
    manager.getSnapshot,
    manager.getSnapshot,
  );

  if (!shouldShowServiceWorkerUpdate(snapshot)) {
    return null;
  }

  const copy = getServiceWorkerUpdateNoticeCopy(snapshot);
  const needsRefresh = snapshot.status === "refresh-required";
  const canDefer = snapshot.status === "ready" || snapshot.status === "blocked";
  const canReview =
    snapshot.canFocusBlocker && snapshot.code === "UNSAVED_WORK";
  const registrationError =
    snapshot.status === "error" &&
    [
      "REGISTRATION_FAILED",
      "UPDATE_CHECK_FAILED",
      "UPDATE_INSTALL_FAILED",
    ].includes(snapshot.code ?? "");

  const tryAgain = () => {
    if (snapshot.status === "activating") {
      return;
    }
    if (registrationError) {
      void manager.checkForUpdate();
      return;
    }
    if (needsRefresh) {
      manager.reloadPortal();
      return;
    }
    void manager.requestActivation();
  };

  const deferAndReturnFocus = () => {
    manager.defer();
    window.requestAnimationFrame(() => {
      focusTarget(document.querySelector("[data-main-focus]"), {
        scroll: false,
      });
    });
  };

  return (
    <aside
      className="portal-update-notice"
      aria-busy={snapshot.status === "activating" ? "true" : undefined}
    >
      <StatusMessage
        className="portal-update-notice__message"
        tone={copy.tone}
        title={copy.title}
      >
        <p>{copy.body}</p>
        <div className="portal-update-notice__actions">
          {canReview && (
            <button
              type="button"
              className="portal-update-notice__button portal-update-notice__button--secondary"
              onClick={() => manager.focusFirstBlocker()}
            >
              Review changes
            </button>
          )}
          <button
            type="button"
            className="portal-update-notice__button"
            aria-disabled={
              snapshot.status === "activating" ? "true" : undefined
            }
            onClick={tryAgain}
          >
            {snapshot.status === "activating"
              ? "Updating portal\u2026"
              : snapshot.status === "ready"
                ? "Update and reload"
                : needsRefresh
                  ? "Reload portal"
                  : "Try again"}
          </button>
          {canDefer && (
            <button
              type="button"
              className="portal-update-notice__button portal-update-notice__button--secondary"
              onClick={deferAndReturnFocus}
            >
              Later
            </button>
          )}
        </div>
      </StatusMessage>
    </aside>
  );
}
